/**
 * Contact discovery: finding which of your phone contacts already use VAV.
 *
 * ## What this can and cannot do
 *
 * Phone numbers live in a space of roughly 10^10. Any scheme where the server
 * can answer "is this number registered?" can, given enough queries, be turned
 * into "enumerate every registered number" — hashing does not fix that, because
 * the attacker can hash candidates too. Signal's answer is to run the lookup
 * inside SGX enclaves so that even they cannot observe it. That needs hardware
 * we do not have.
 *
 * So this design does not pretend to hide the numbers you look up. Instead it
 * makes bulk harvesting expensive and detectable:
 *
 *  - numbers are normalised then hashed with a slow KDF, so a stolen database
 *    cannot be reversed by hashing a phone book in an afternoon;
 *  - a per-deployment pepper means one leaked database does not help against
 *    another server;
 *  - lookups are batched, rate limited and logged by volume, so a client
 *    grinding through the number space stands out.
 *
 * Registering a number is opt-in, and the README says plainly what the server
 * learns. A user who does not want to be findable simply does not register one.
 */
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js';
import { concatBytes, kdf, sha256, toB64, utf8 } from './primitives.ts';

const DISCOVERY_INFO = 'VAV-Discovery-v1';
/** Deliberately slow: this is the cost an attacker pays per candidate number. */
const HASH_ROUNDS = 20_000;

/**
 * Reduce a number to canonical E.164 so that the same phone written five ways
 * produces one hash.
 *
 * This used to be hand-rolled digit surgery against a single default country
 * code, which worked for one market and quietly mismatched everywhere else.
 * Trunk prefixes are not universally `0` (Italy keeps a leading zero *inside*
 * the national number; Argentina inserts a `9` for mobiles), national number
 * lengths vary by country and by carrier, and "starts with the country code"
 * is genuinely ambiguous in several plans. Getting any of that wrong does not
 * throw -- it silently returns a hash that matches nobody, so two real users
 * simply never find each other and nothing in the logs says why.
 *
 * `region` is a *hint* for numbers written in national format, nothing more. A
 * number already in international form (`+…` or `00…`) ignores it entirely, so
 * a Brazilian address book opened by a user whose region is guessed wrong still
 * resolves correctly for every contact stored internationally.
 */
export function normalisePhone(input: string, region?: string): string | undefined {
  if (!input || !input.trim()) return undefined;

  const parsed = parsePhoneNumberFromString(input, normaliseRegion(region));
  // isValid() checks the number against that country's plan, not just its
  // length -- it rejects the transposed digit that a length check waves through.
  if (!parsed || !parsed.isValid()) return undefined;

  // `.number` is E.164 with the leading '+'. Store digits only, so the stored
  // form cannot drift on whether a caller kept the plus.
  return parsed.number.replace(/^\+/, '');
}

/**
 * Region codes are ISO 3166-1 alpha-2 (`IL`, `BR`, `IN`), not calling codes.
 *
 * Deployments configured before this change may still pass a calling code like
 * `972`, so one is translated rather than silently ignored. This only works
 * where the mapping is unambiguous: `972` is Israel and nothing else, but `44`
 * is also Guernsey, the Isle of Man and Jersey, and `1` covers the US, Canada
 * and twenty Caribbean countries. Where it is ambiguous we return no hint
 * rather than guess -- a wrong region parses a national number into a
 * different, real, wrong number, which is worse than refusing it.
 */
function normaliseRegion(region?: string): CountryCode | undefined {
  if (!region) return undefined;
  const trimmed = region.trim();
  if (/^[A-Za-z]{2}$/.test(trimmed)) return trimmed.toUpperCase() as CountryCode;

  const digits = trimmed.replace(/^\+/, '');
  if (/^\d+$/.test(digits)) {
    const countries = getCountries().filter((c) => getCountryCallingCode(c) === digits);
    // Several countries share a calling code (+1 alone covers twenty-odd). Only
    // accept an unambiguous mapping; guessing would be worse than no hint.
    if (countries.length === 1) return countries[0];
  }
  return undefined;
}

/**
 * Slow hash of a normalised number.
 *
 * The pepper is a server-wide secret published to clients — it is not a
 * password, it just ensures hashes are useless against a different deployment.
 */
export function hashPhone(normalised: string, pepper: string): string {
  let h = concatBytes(utf8(DISCOVERY_INFO), utf8(pepper), utf8(normalised));
  for (let i = 0; i < HASH_ROUNDS; i++) h = sha256(h);
  // Truncated to 16 bytes: still far beyond collision range for any realistic
  // user count, and it halves what a database leak exposes.
  return toB64(kdf(h, utf8(pepper), DISCOVERY_INFO, 16));
}

/**
 * The number as it will be stored, in readable international form.
 *
 * Exists so the UI can show the user what their input resolved to *before* they
 * commit it. Typing `+972 054 …` and being told only "saved" gives no way to
 * tell whether the stray trunk zero was handled -- and it is exactly the kind
 * of doubt that makes someone abandon linking altogether.
 */
export function formatPhoneForDisplay(input: string, region?: string): string | undefined {
  const parsed = parsePhoneNumberFromString(input, normaliseRegion(region));
  if (!parsed || !parsed.isValid()) return undefined;
  return parsed.formatInternational();
}

/**
 * Synchronous, for callers with a handful of numbers and no interface to keep
 * alive. Anything reading an address book wants `hashPhonesAsync`.
 *
 * Reduces before hashing for the same two reasons as the async version: a
 * duplicate should not cost 20,000 rounds to be recognised as one, and both
 * functions must agree on which spelling of a number they hand back.
 */
export function hashPhones(numbers: string[], pepper: string, region?: string): Map<string, string> {
  const byHash = new Map<string, string>();
  for (const [normalised, original] of uniqueNumbers(numbers, region)) {
    byHash.set(hashPhone(normalised, pepper), original);
  }
  return byHash;
}

/**
 * Valid numbers in canonical form, mapped to the first spelling the user had.
 *
 * The first rather than the last: it is the one they will recognise in a list of
 * results, and picking arbitrarily would make two functions disagree about the
 * same address book.
 */
function uniqueNumbers(numbers: string[], region?: string): Map<string, string> {
  const unique = new Map<string, string>();
  for (const number of numbers) {
    const normalised = normalisePhone(number, region);
    if (normalised && !unique.has(normalised)) unique.set(normalised, number);
  }
  return unique;
}

/**
 * A cheap, stable key for one number, safe to write to disk.
 *
 * The expensive hash could be cached against the number itself, but
 * `contacts.ts` promises the address book is never written down, and a stored
 * map of the user's contacts would quietly break that. One SHA-256 is nothing
 * beside 20,000, and it keeps the cache opaque: it holds digests and results,
 * never a phone number.
 */
export function discoveryCacheKey(normalised: string, pepper: string): string {
  return toB64(sha256(concatBytes(utf8('VAV-Discovery-Cache-v1'), utf8(pepper), utf8(normalised))));
}

export interface HashPhonesOptions {
  /**
   * Progress over *unique, valid* numbers — the ones that actually cost
   * anything. Reporting against the raw entry count would stall visibly on an
   * address book full of duplicates and non-numbers.
   */
  onProgress?: (done: number, total: number) => void;
  /** Checked between numbers; anything with an `aborted` flag will do. */
  signal?: { aborted: boolean };
  /** Called once the address book has been reduced to unique valid numbers. */
  onScanned?: (unique: number, entries: number) => void;
  /**
   * Results kept between runs, keyed by `discoveryCacheKey`. A hit skips the
   * 20,000 rounds entirely, which is what makes a second import fast.
   */
  cache?: {
    get: (key: string) => string | undefined;
    set: (key: string, hash: string) => void;
  };
}

/**
 * The same hashes, without freezing the interface.
 *
 * `hashPhone` is 20,000 rounds by design and an address book is hundreds of
 * numbers, so the synchronous version owns the only JavaScript thread for tens
 * of seconds — minutes under Hermes, which has no native SHA-256 and runs this
 * in plain JS. Nothing repaints in that window: the spinner stops turning,
 * touches queue up, and an import that is working perfectly is indistinguishable
 * from a hang. That is what "looks stuck" was.
 *
 * The cost itself stays exactly as it is. It is what an attacker pays per
 * candidate number, and lowering it would invalidate every hash already stored.
 * The design asks for bulk lookup to be slow *and visible*; this supplies the
 * missing half by yielding between numbers so the UI can paint, and by saying
 * how far along it is.
 *
 * Yielding per number rather than per batch: one number is ~25 ms on a laptop
 * and several times that on a phone, already longer than a frame. Batching would
 * save a negligible number of task hops and buy back the jank.
 */
export async function hashPhonesAsync(
  numbers: string[],
  pepper: string,
  region?: string,
  { onProgress, onScanned, signal, cache }: HashPhonesOptions = {},
): Promise<Map<string, string>> {
  // Reduce first, hash second. The old order normalised each entry and hashed it
  // immediately, deduplicating on the *result* — so one number stored three ways
  // paid 60,000 rounds to discover it was one number. A synced address book is
  // mostly duplicates and entries that are not numbers at all, and normalising
  // is thousands of times cheaper than hashing, so nothing should reach the hash
  // until it is known to be both valid and new.
  const unique = new Map<string, string>();
  for (let i = 0; i < numbers.length; i += SCAN_CHUNK) {
    if (signal?.aborted) return new Map();
    for (const [normalised, original] of uniqueNumbers(numbers.slice(i, i + SCAN_CHUNK), region)) {
      if (!unique.has(normalised)) unique.set(normalised, original);
    }
    // Parsing is fast but not free, and 65,000 of them is still a long freeze.
    await yieldToUi();
  }
  onScanned?.(unique.size, numbers.length);

  const byHash = new Map<string, string>();
  let done = 0;
  let lastBreath = Date.now();

  for (const [normalised, original] of unique) {
    if (signal?.aborted) break;

    const key = discoveryCacheKey(normalised, pepper);
    let hash = cache?.get(key);
    if (hash === undefined) {
      hash = hashPhone(normalised, pepper);
      cache?.set(key, hash);
    }
    byHash.set(hash, original);
    done += 1;

    // Yield on a clock, not on every number. Yielding per number sounds gentler
    // and is far worse: `setTimeout(…, 0)` is not zero on a phone, and every
    // progress report re-renders the screen, so each number paid a scheduler
    // round trip plus a full React pass. Measured on a device that overhead was
    // larger than the hashing it was interleaving with.
    //
    // A breath every ~120 ms keeps the UI at roughly eight paints a second —
    // past the point where anyone can tell — while letting a run of numbers
    // share one interruption.
    if (Date.now() - lastBreath >= BREATH_MS) {
      onProgress?.(done, unique.size);
      await yieldToUi();
      lastBreath = Date.now();
    }
  }

  // The last partial group would otherwise never be reported, leaving the
  // counter short of the total on a finished import.
  onProgress?.(done, unique.size);
  return byHash;
}

/** Entries parsed between yields. Parsing is ~1000× cheaper than hashing. */
const SCAN_CHUNK = 200;

/** How long to work before letting the interface paint. */
const BREATH_MS = 120;

/** Hand the thread back so React can paint what was just reported. */
function yieldToUi(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}
